import Image from 'next/legacy/image';
import { ScreenName } from 'common/utils/pp_tracking';
import PostItem from 'components/v2/modules/post-item';
import ContentLoader from 'react-content-loader';
import { PostItemType } from 'service/timeline';

interface PostListProps {
  posts: PostItemType[];
  screen: ScreenName;
  isFetched: boolean;
  noPostMessage: string;
  isHtmlContent?: boolean;
  hidePinPost?: boolean;
}

// Empty state component
const EmptyPostState = ({ message }: { message: string }) => (
  <div className="flex min-h-[250px] w-full flex-col items-center justify-center rounded-lg bg-white p-4 text-center shadow-sm hover:shadow-md">
    <Image src="/images/icons/empty.svg" alt="empty" width={100} height={100} />
    <div
      className="mt-4 text-base font-semibold text-gray-600"
      dangerouslySetInnerHTML={{ __html: message }}
    />
  </div>
);

const PostList = ({
  posts,
  screen,
  isFetched,
  noPostMessage,
  isHtmlContent = false,
  hidePinPost = false,
}: PostListProps) => {
  const filteredPosts = hidePinPost
    ? posts?.filter((post) => !post.is_pinned)
    : posts;
  if (!isFetched) return <PostListSkeleton />;

  return (
    <div className="flex flex-col gap-5 pb-20">
      {filteredPosts.map((post, index) => (
        <PostItem
          key={`${post.id}-${index}`}
          post={post}
          screen={screen}
          isHtmlContent={isHtmlContent}
        />
      ))}
      {filteredPosts.length === 0 && noPostMessage && (
        <EmptyPostState message={noPostMessage} />
      )}
    </div>
  );
};

// Skeleton loading component for post item
const PostItemSkeleton = () => (
  <div className="overflow-hidden rounded-lg border border-gray-100 bg-white p-2 shadow-sm sm:p-4">
    <ContentLoader
      speed={1.5}
      width="100%"
      height={420}
      uniqueKey="post-item-skeleton"
      viewBox="0 0 560 420"
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
      className="w-full max-w-full"
      preserveAspectRatio="xMidYMid meet"
    >
      {/* User avatar */}
      <circle cx="24" cy="24" r="20" />

      {/* User name and info */}
      <rect x="52" y="10" rx="4" ry="4" width="120" height="12" />
      <rect x="180" y="10" rx="4" ry="4" width="60" height="12" />
      <rect x="52" y="28" rx="4" ry="4" width="80" height="10" />

      {/* Follow button */}
      <rect x="480" y="8" rx="16" ry="16" width="64" height="32" />

      {/* Post text content */}
      <rect x="0" y="65" rx="4" ry="4" width="540" height="14" />
      <rect x="0" y="85" rx="4" ry="4" width="480" height="14" />
      <rect x="0" y="105" rx="4" ry="4" width="420" height="14" />

      {/* Continue reading link */}
      <rect x="0" y="130" rx="4" ry="4" width="80" height="12" />

      {/* Media/Video area */}
      <rect x="0" y="155" rx="8" ry="8" width="560" height="200" />

      {/* Play button overlay for video */}
      <circle cx="280" cy="255" r="24" fill="#ffffff80" />
      <polygon points="272,245 272,265 292,255" fill="#000000" />

      {/* Action buttons area */}
      <rect x="0" y="375" rx="4" ry="4" width="60" height="12" />
      <rect x="80" y="375" rx="4" ry="4" width="80" height="12" />
      <rect x="180" y="375" rx="4" ry="4" width="60" height="12" />

      {/* More options */}
      <circle cx="530" cy="381" r="3" />
      <circle cx="540" cy="381" r="3" />
      <circle cx="550" cy="381" r="3" />
    </ContentLoader>
  </div>
);

// Multiple skeleton posts
const PostListSkeleton = () => (
  <div className="flex flex-col gap-5 overflow-x-hidden">
    {Array.from({ length: 10 }).map((_, index) => (
      <PostItemSkeleton key={`skeleton-${index}`} />
    ))}
  </div>
);

export default PostList;
